import type { ScrollFadeAxis } from '#layers/ui/constants/scroll-fade';
import { ScrollFadeAxis as Axis, MAX_SCROLL_FADE } from '#layers/ui/constants/scroll-fade';

/** The subset of an element a fade is measured from; satisfied by any `HTMLElement`. */
export interface ScrollFadeSource {
	scrollTop: number;
	scrollLeft: number;
	scrollHeight: number;
	scrollWidth: number;
	clientHeight: number;
	clientWidth: number;
}

export interface ScrollFadeOptions {
	axis: ScrollFadeAxis;
	/** Set when the container's own direction is RTL, which flips how `scrollLeft` is reported. */
	isRtl?: boolean;
}

export interface ScrollFade {
	/** Fade size in pixels for the physical top or left edge. */
	start: number;
	/** Fade size in pixels for the physical bottom or right edge. */
	end: number;
}

/**
 * How large the fade over each end of a scroll container should be, so clipped content reads
 * as continuing past the edge. Each fade grows with the distance already scrolled away from
 * its edge, which keeps it from snapping to full size the instant the container starts
 * moving. Positions are clamped because elastic overscroll reports a position outside the
 * scrollable range.
 *
 * Sizes are physical rather than logical so callers can bind static `mask-*` classes, which
 * Tailwind only offers per physical edge.
 */
export function getScrollFade(source: ScrollFadeSource, { axis, isRtl = false }: ScrollFadeOptions): ScrollFade {
	const isVertical = axis === Axis.Vertical;
	const scrollSize = isVertical ? source.scrollHeight : source.scrollWidth;
	const clientSize = isVertical ? source.clientHeight : source.clientWidth;
	const maxScroll = Math.max(scrollSize - clientSize, 0);

	// An RTL container reports `scrollLeft` across [-maxScroll, 0], counting up as the viewport
	// moves right, so shift it back into a distance from the physical left edge.
	const offset = isVertical ? source.scrollTop : source.scrollLeft;
	const distanceFromStart = !isVertical && isRtl ? maxScroll + offset : offset;
	const position = Math.min(Math.max(distanceFromStart, 0), maxScroll);

	return {
		start: Math.min(position, MAX_SCROLL_FADE),
		end: Math.min(maxScroll - position, MAX_SCROLL_FADE),
	};
}
